import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { useIsFetching } from "@tanstack/react-query";
import { getUserMeetingRoleFromMeeting } from "@platform/utils/getUserMeetingRoleFromMeeting";
import { useUpdateUserPhaseActionStatus } from "@platform/hooks/useUpdateUserPhaseActionStatus";
import { useSupportSheet } from "@platform/features/support/hooks/useSupportSheet";
import { useGetOrCreateQuizResponse } from "@platform/features/quiz/hooks/useGetOrCreateQuizResponse";
import { useDialog } from "@shared/features/dialog/useDialog";
import { usePlatform } from "@platform/PlatformContext";
import { PhaseAction } from "@platform/types/platform.types";
import { TimezoneOption } from "@platform/features/timezones/timezones.types";

export function useProgramPhaseAction(
  action: PhaseAction,
  userTimezoneOption: TimezoneOption
) {
  const { user } = usePlatform();

  let actionBadge;

  const quizMutation = useGetOrCreateQuizResponse({
    quizId:
      action.type === "matching_quiz"
        ? action.target_object_data?.quiz
        : action.target_object?.id,
    userId: user?.id,
    status: "in_progress",
    type: action.type,
  });

  const isQuizFetching = useIsFetching({
    queryKey: ["quiz-response", action.target_object?.id],
  });

  const { mutate: setToInProgressUserPhaseActionStatus } =
    useUpdateUserPhaseActionStatus({
      userPhaseActionId: parseInt(action.id, 10),
      status: "in_progress",
    });

  const { mutate: completeUserPhaseActionStatus } =
    useUpdateUserPhaseActionStatus({
      userPhaseActionId: parseInt(action.id, 10),
      status: "completed",
    });

  const { openSheet } = useSupportSheet();

  const navigate = useNavigate();

  const { openDialog } = useDialog();

  let actionLabel = action.cta_label;

  let dialogName = "";

  let props = {};

  let disabled = false;

  let onActionClick = () => {};

  const { status, closed_date, due_date } = action;

  const isActionCompleted = status === "completed";

  // Validate if closed_date is a valid date
  const isValidClosedDate = moment(closed_date).isValid();

  const isActionAvailable =
    isValidClosedDate && moment().isBefore(moment(closed_date));

  const isValidDueDate = moment(due_date).isValid();

  const dueDate = isValidDueDate
    ? moment(due_date).tz(userTimezoneOption.name).format("MMM Do")
    : null;

  let statusLabel = isActionCompleted
    ? "Completed"
    : dueDate && isValidDueDate
    ? `Due ${dueDate}`
    : null;

  // Friend Invite Form Dialog
  // if (action.slug === "send_friend_invite" && action.status === "not_started") {
  //   actionLabel = "Invite";
  //   dialogName = "invitationSend";
  // }

  // Friend Invite Deletion Dialog
  // if (action.slug === "send_friend_invite" && action.status === "completed") {
  //   actionLabel = "Manage";
  //   dialogName = "invitationDelete";
  // }

  // Matching Survey
  if (action.type === "matching_quiz") {
    dialogName = "quiz";

    props = {
      quizResponseId: action.target_object.id,
      userPhaseActionId: action.id,
    };

    actionLabel = isQuizFetching ? "Loading..." : action.cta_label;

    onActionClick = () => openDialog(dialogName, props);
  }

  // Meeting Survey
  if (action.type === "meeting_survey") {
    dialogName = "quiz";

    props = {
      quizResponseId: action.target_object.id,
      userPhaseActionId: action.id,
    };

    actionLabel = action.cta_label || "Take Survey";

    onActionClick = async () => {
      const result = await quizMutation?.mutateAsync();

      openDialog(dialogName, {
        quizResponseId: result.quiz_response.id,
        userPhaseActionId: action.id,
      });

      setToInProgressUserPhaseActionStatus();
    };
  }

  // Scheduling Form Dialog
  if (action.type === "do_schedule") {
    dialogName = "scheduling";

    props = {
      userPhaseActionId: action.id,
    };

    actionLabel = action.status === "completed" ? "Edit" : action.cta_label;

    onActionClick = () => openDialog(dialogName, props);
  }

  if (action.type === "meeting") {
    const role = getUserMeetingRoleFromMeeting(
      action.binding_object_data,
      user?.id || null
    );

    if (role) {
      actionBadge = role.name;
    }

    statusLabel = isValidDueDate
      ? moment(due_date).tz(userTimezoneOption.name).format("MMM Do h:mma z")
      : null;

    onActionClick = () => navigate(`/video/join/${action.target_object.id}`);
  }

  if (
    ["meeting_script", "meeting_reading", "article_informational"].includes(
      action.type
    )
  ) {
    actionLabel = action.cta_label || "Read";

    onActionClick = () => {
      openSheet(action.target_object_data.slug);
      completeUserPhaseActionStatus();
    };
  }

  // Workbook Download
  if (action.type === "workbook_download") {
    onActionClick = () => {
      completeUserPhaseActionStatus();
      window.open(action.cta_action_url, "_blank");
    };

    statusLabel = isActionCompleted ? "Completed" : null;
  }

  // Orientation RSVP
  if (action.type === "orientation_rsvp") {
    onActionClick = () => {
      completeUserPhaseActionStatus();
      window.open(action.cta_action_url, "_blank");
    };

    statusLabel = null;
  }

  // Standalone Meeting
  if (action.type === "meeting_standalone") {
    statusLabel = isActionCompleted ? "Completed" : null;

    onActionClick = () =>
      navigate(`/video/join/${action.binding_object_data.id}`);
  }

  return {
    actionLabel,
    actionBadge,
    onActionClick,
    isActionDisabled: disabled,
    statusLabel,
    isActionCompleted,
    isActionAvailable,
  };
}

export default useProgramPhaseAction;
